import { useEffect } from "react";

/**
 * SEOHead — Composant SEO réutilisable pour toutes les landing pages.
 *
 * Injecte dans <head> :
 *  - <title>
 *  - <meta name="description" | "keywords" | "robots">
 *  - Open Graph (og:title, og:description, og:image, og:url, og:type, og:locale)
 *  - Twitter Card (summary_large_image)
 *  - <link rel="canonical">
 *  - JSON-LD Schema.org (LocalBusiness, Service, FAQPage)
 *
 * À utiliser SYSTÉMATIQUEMENT sur chaque nouvelle landing page.
 *
 * @example
 * <SEOHead
 *   title="..."
 *   description="..."
 *   keywords="kw1, kw2"
 *   canonical="https://emgroupdigital.fr/ma-page"
 *   ogImage="https://emgroupdigital.fr/og/ma-page.jpg"
 *   locale="fr_FR"          // fr_FR (Réunion) | fr_MU (Maurice) | en_MU
 *   robots="index, follow"
 *   localBusiness={{ name, description, areaServed, email, telephone, address, url }}
 *   serviceSchema={{ name, description, areaServed, price, priceCurrency }}
 *   faqSchema={[{ q, a }, ...]}
 * />
 */

function setMeta(name, content, attr = "name") {
  if (typeof document === "undefined" || content == null || content === "") return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  if (typeof document === "undefined" || !href) return;
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function setAlternates(alternates) {
  if (typeof document === "undefined") return;
  // Remove previous hreflang links to avoid mixing between routes
  document
    .querySelectorAll('link[rel="alternate"][data-emg-hreflang]')
    .forEach((el) => el.remove());
  if (!alternates || alternates.length === 0) return;
  for (const alt of alternates) {
    if (!alt || !alt.href || !alt.hreflang) continue;
    const link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", alt.hreflang);
    link.setAttribute("href", alt.href);
    link.setAttribute("data-emg-hreflang", "true");
    document.head.appendChild(link);
  }
}

function setJsonLd(id, payload) {
  if (typeof document === "undefined") return;
  const existing = document.getElementById(id);
  if (!payload) {
    if (existing) existing.remove();
    return;
  }
  let el = existing;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

function setHtmlLang(lang) {
  if (typeof document === "undefined" || !lang) return;
  document.documentElement.setAttribute("lang", lang);
}

function buildLocalBusinessLd(lb) {
  if (!lb) return null;
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: lb.name || "EM Group",
    description: lb.description,
    email: lb.email || "contact@emgroupdigital.fr",
    telephone: lb.telephone,
    url: lb.url,
    image: lb.image,
    address: lb.address || {
      "@type": "PostalAddress",
      addressCountry: "FR",
    },
    areaServed: Array.isArray(lb.areaServed)
      ? lb.areaServed.map((a) => ({ "@type": "Place", name: a }))
      : lb.areaServed
      ? { "@type": "Place", name: lb.areaServed }
      : undefined,
  };
}

function buildServiceLd(svc, lb) {
  if (!svc) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: svc.name,
    serviceType: svc.serviceType || svc.name,
    description: svc.description,
    areaServed: svc.areaServed
      ? { "@type": "AdministrativeArea", name: svc.areaServed }
      : undefined,
    provider: {
      "@type": "LocalBusiness",
      name: (lb && lb.name) || "EM Group",
      email: (lb && lb.email) || "contact@emgroupdigital.fr",
      url: (lb && lb.url) || "https://emgroupdigital.fr",
    },
    offers: svc.price
      ? {
          "@type": "Offer",
          price: svc.price,
          priceCurrency: svc.priceCurrency || "EUR",
          description: svc.priceDescription,
        }
      : undefined,
  };
}

function buildFaqLd(items) {
  if (!items || !items.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

function buildArticleLd(article) {
  if (!article) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.headline,
    description: article.description,
    image: article.image,
    inLanguage: article.inLanguage,
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: article.author || { "@type": "Organization", name: "EM Group" },
    publisher: {
      "@type": "Organization",
      name: "EM Group",
      logo: {
        "@type": "ImageObject",
        url: "https://emgroupdigital.fr/brand/logo-em-group.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": article.canonical,
    },
    articleSection: article.section,
    keywords: article.keywords,
  };
}

function buildBreadcrumbLd(items) {
  if (!items || !items.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.item,
    })),
  };
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogType = "website",
  locale = "fr_FR",
  htmlLang = "fr",
  robots = "index, follow",
  localBusiness,
  serviceSchema,
  faqSchema,
  articleSchema,
  breadcrumbSchema,
  alternates,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    const prevLang = document.documentElement.getAttribute("lang");

    if (title) document.title = title;
    setHtmlLang(htmlLang);

    setMeta("description", description);
    setMeta("keywords", keywords);
    setMeta("robots", robots);

    // Open Graph
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:locale", locale, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:site_name", "EM Group", "property");

    // Twitter Card
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);

    // Canonical
    setCanonical(canonical);

    // Alternate hreflang links (FR/EN blog articles)
    setAlternates(alternates);

    // JSON-LD
    setJsonLd("ld-local-business", buildLocalBusinessLd(localBusiness));
    setJsonLd("ld-service", buildServiceLd(serviceSchema, localBusiness));
    setJsonLd("ld-faq", buildFaqLd(faqSchema));
    setJsonLd("ld-article", buildArticleLd(articleSchema));
    setJsonLd("ld-breadcrumb", buildBreadcrumbLd(breadcrumbSchema));

    return () => {
      document.title = prevTitle;
      if (prevLang) document.documentElement.setAttribute("lang", prevLang);
      // Nettoyage des JSON-LD propres à la page pour éviter le mélange entre routes
      ["ld-local-business", "ld-service", "ld-faq", "ld-article", "ld-breadcrumb"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
      // Nettoyage des hreflang alternates
      document
        .querySelectorAll('link[rel="alternate"][data-emg-hreflang]')
        .forEach((el) => el.remove());
    };
  }, [
    title,
    description,
    keywords,
    canonical,
    ogImage,
    ogType,
    locale,
    htmlLang,
    robots,
    JSON.stringify(localBusiness || null),
    JSON.stringify(serviceSchema || null),
    JSON.stringify(faqSchema || null),
    JSON.stringify(articleSchema || null),
    JSON.stringify(breadcrumbSchema || null),
    JSON.stringify(alternates || null),
  ]);

  return null;
}
